import { eventChannel } from 'redux-saga';
import { all, call, fork, put, take, takeEvery } from 'redux-saga/effects';
import { auth } from 'firebase/firebase';

import { AUTH_TOKEN_LISTENER_REGISTER, SIGNOUT_USER_SUCCESS } from 'constants/ActionTypes';
import { isLocationAlertPublicPathname } from '@src/containers/App/LocationAlert/locationAlert.utils';
import { isMarkingPortalPublicAuthPathname } from '@src/containers/App/MarkingPortal/markingPortal.utils';
import {
  errorNotification,
  userAccessFetch,
  userAccessReset,
  userOrgUserReset,
  userProfileFetch,
  userProfileReset,
  userSignOut,
} from 'appRedux/actions';

let authTokenChannel = null;

function closeEventChannel() {
  if (authTokenChannel) {
    authTokenChannel.close();
  }
  authTokenChannel = null;
}

/**
 * Creates (if it doesn't exist) and returns an event channel that listens for changes in the authenticated user's token.
 * When the token changes, it emits the authenticated user if the user is on a public route
 * (/signin, /marking-portal, /locationAlert).
 * Otherwise, if there is no user and the path is not public, it emits a credentials error.
 */
function openEventChannel() {
  if (!authTokenChannel) {
    authTokenChannel = eventChannel(emit => {
      const unsubscribe = auth.onIdTokenChanged(user => {
        const pathname = window.location.pathname;
        const isPublicAuthPath =
          pathname === '/signin' ||
          isMarkingPortalPublicAuthPathname(pathname) ||
          isLocationAlertPublicPathname(pathname);

        // If not on a public auth path and no user, emit credentials error; otherwise, emit the user object.
        const credentialsError = !isPublicAuthPath && new Error('Error with stored credentials.');

        emit(user || credentialsError);
      });

      // Clean-up function to remove the listener if the channel is closed
      return unsubscribe;
    });
  }
  return authTokenChannel;
}

// Everytime we receive a state change:
function* listenerRegister() {
  const channel = yield call(openEventChannel);

  while (true) {
    // Take means the saga will block until action is dispatched
    try {
      const result = yield take(channel);
      if (result) {
        yield put(userProfileFetch());
        yield put(userAccessFetch());
      } else {
        yield put(userProfileReset());
        yield put(userAccessReset());
        yield put(userOrgUserReset());
      }
    } catch (error) {
      yield put(userProfileReset());
      yield put(userAccessReset());
      yield put(userOrgUserReset());
      yield put(errorNotification(error.toString()));
      yield put(userSignOut());
    }
  }
}

export function* listenerUnregister() {
  yield call(closeEventChannel);
}

function* filterListenerRegister() {
  yield takeEvery(AUTH_TOKEN_LISTENER_REGISTER, listenerRegister);
}

function* filterListenerUnregister() {
  yield takeEvery(SIGNOUT_USER_SUCCESS, listenerUnregister);
}

export default function* rootSaga() {
  yield all([fork(filterListenerRegister), fork(filterListenerUnregister)]);
}
